/**
 * Iguastats - Group Stats Tracker Frontend
 * React application for tracking group statistics
 */

// React imports
import React from 'react';
import ReactDOM from 'react-dom/client';

// Recharts components for data visualization
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line 
} from 'recharts';

// Styles
import './index.css';

// ============================================================================
// APP ENTRY POINT
// ============================================================================

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);

/**
 * Main App Component
 * Manages authentication state and routes to appropriate views
 */
function App() {
  // Get token and user from localStorage (persists across page refreshes)
  const [token, setToken] = React.useState(localStorage.getItem('token'));
  const [user, setUser] = React.useState(localStorage.getItem('user'));

  // Handle successful login
  const handleLogin = (newToken, username) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', username);
    setToken(newToken);
    setUser(username);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  // Show auth screen if not logged in
  if (!token) {
    return <Auth onLogin={handleLogin} />;
  }

  // Show dashboard if logged in
  return <Dashboard token={token} user={user} onLogout={handleLogout} />;
}

// ============================================================================
// AUTHENTICATION COMPONENT
// ============================================================================

/**
 * Auth Component
 * Handles user registration and login
 */
function Auth({ onLogin }) {
  // Toggle between login and register modes
  const [isRegister, setIsRegister] = React.useState(false);
  
  // Form state
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');

  /**
   * Handle form submission
   * Calls either register or login API endpoint
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Determine endpoint based on mode
    const endpoint = isRegister ? '/api/register' : '/api/login';
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });

    const data = await res.json();
    if (!res.ok) {
      setError(data.error || 'An error occurred');
      return;
    }

    // If registering, switch to login mode
    if (isRegister) {
      setIsRegister(false);
      setError('Registration successful! Please login.');
    } else {
      // If logging in, store token and user
      onLogin(data.token, data.username);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h1>IguaStats</h1>
        <p className="subtitle">Track group stats with friends</p>
        
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button type="submit">{isRegister ? 'Register' : 'Login'}</button>
        </form>

        {error && <p className="error">{error}</p>}

        <p className="toggle-auth">
          {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button className="link-btn" onClick={() => setIsRegister(!isRegister)}>
            {isRegister ? 'Login' : 'Register'}
          </button>
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// DASHBOARD COMPONENT
// ============================================================================

/**
 * Dashboard Component
 * Main application view with tabs and data display
 */
function Dashboard({ token, user, onLogout }) {
  // State for all data needed by the app
  const [users, setUsers] = React.useState([]);
  const [statTypes, setStatTypes] = React.useState([]);
  const [summary, setSummary] = React.useState([]);
  const [userStats, setUserStats] = React.useState([]);
  const [groupStats, setGroupStats] = React.useState([]);
  const [recentStats, setRecentStats] = React.useState([]);
  const [pairStats, setPairStats] = React.useState([]);
  const [timelineStats, setTimelineStats] = React.useState([]);
  
  // UI state
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('overview');

  /**
   * Fetch all data from API
   * Called on mount and after data modifications
   */
  const fetchData = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    
    // Fetch all data in parallel for better performance
    const [usersRes, typesRes, summaryRes, userStatsRes, groupStatsRes, recentRes, pairRes, timelineRes] = await Promise.all([
      fetch('/api/users', { headers }),
      fetch('/api/stat-types', { headers }),
      fetch('/api/stats/summary', { headers }),
      fetch('/api/stats/by-user', { headers }),
      fetch('/api/stats/by-group', { headers }),
      fetch('/api/stats', { headers }),
      fetch('/api/stats/by-pair', { headers }),
      fetch('/api/stats/timeline', { headers })
    ]);

    setUsers(await usersRes.json());
    setStatTypes(await typesRes.json());
    setSummary(await summaryRes.json());
    setUserStats(await userStatsRes.json());
    setGroupStats(await groupStatsRes.json());
    setRecentStats(await recentRes.json());
    setPairStats(await pairRes.json());
    setTimelineStats(await timelineRes.json());
  };

  // Fetch data on component mount
  React.useEffect(() => {
    fetchData();
  }, [token]);

  return (
    <div className="dashboard">
      {/* Header with title and user info */}
      <header>
        <h1>IguaStats</h1>
        <div className="header-right">
          <span>Welcome, {user}</span>
          <button className="logout-btn" onClick={onLogout}>Logout</button>
        </div>
      </header>

      {/* Tab navigation */}
      <nav className="tabs">
        <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>Overview</button>
        <button className={activeTab === 'by-user' ? 'active' : ''} onClick={() => setActiveTab('by-user')}>By Person</button>
        <button className={activeTab === 'stats' ? 'active' : ''} onClick={() => setActiveTab('stats')}>Stats</button>
        <button className={activeTab === 'recent' ? 'active' : ''} onClick={() => setActiveTab('recent')}>Recent</button>
      </nav>

      {/* Main content area - shows different views based on active tab */}
      <main>
        {activeTab === 'overview' && <OverviewSummary summary={summary} />}
        {activeTab === 'by-user' && <UserStats userStats={userStats} users={users} statTypes={statTypes} />}
        {activeTab === 'stats' && <StatsTab summary={summary} userStats={userStats} pairStats={pairStats} timelineStats={timelineStats} users={users} />}
        {activeTab === 'recent' && <RecentStats stats={recentStats} users={users} statTypes={statTypes} token={token} onRefresh={fetchData} />}
      </main>

      {/* Floating action button to add new stat */}
      <button className="fab" onClick={() => setShowAddModal(true)}>+ Add Stat</button>

      {/* Add Stat Modal */}
      {showAddModal && (
        <AddStatModal
          token={token}
          users={users}
          statTypes={statTypes}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// OVERVIEW SUMMARY COMPONENT
// ============================================================================

/**
 * OverviewSummary Component
 * Shows summary cards with totals per stat type
 */
function OverviewSummary({ summary }) {
  return (
    <div className="summary-grid">
      {summary.length === 0 ? (
        <p className="empty-state">No stats yet. Add your first stat!</p>
      ) : (
        summary.map(s => (
          <div key={s.stat_type_id} className="stat-card">
            <h3>{s.stat_type}</h3>
            <div className="stat-value">{s.total}</div>
            <div className="stat-count">{s.count} entries</div>
          </div>
        ))
      )}
    </div>
  );
}

// ============================================================================
// USER STATS COMPONENT
// ============================================================================

/**
 * UserStats Component
 * Shows individual stats for each user
 */
function UserStats({ userStats, users, statTypes }) {
  // Build user data structure from stats
  const userData = {};
  userStats.forEach(s => {
    if (!userData[s.user_id]) {
      userData[s.user_id] = { username: s.username, stats: {} };
    }
    userData[s.user_id].stats[s.stat_type_id] = {
      name: s.stat_type,
      total: s.total,
      count: s.count
    };
  });

  return (
    <div className="user-stats">
      {Object.entries(userData).length === 0 ? (
        <p className="empty-state">No stats yet.</p>
      ) : (
        Object.entries(userData).map(([userId, data]) => (
          <div key={userId} className="user-card">
            <h3>{data.username}</h3>
            <div className="user-stat-grid">
              {Object.values(data.stats).map((stat, i) => (
                <div key={i} className="mini-stat">
                  <span className="mini-stat-name">{stat.name}</span>
                  <span className="mini-stat-value">{stat.total}</span>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ============================================================================
// STATS TAB COMPONENT (CHARTS)
// ============================================================================

/**
 * StatsTab Component
 * Displays charts and analytics visualizations
 */
function StatsTab({ summary, userStats, pairStats, timelineStats, users }) {
  // Destructure Recharts components for use in JSX
  // const { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } = Recharts;

  // Color palette for charts
  const COLORS = ['#667eea', '#f0ad4e', '#5cb85c', '#d9534f', '#5bc0de', '#f7cac9', '#92a8d1', '#955251', '#b565a7', '#009b77'];

  // Process user stats data for bar chart
  const userData = React.useMemo(() => {
    const byUser = {};
    userStats.forEach(s => {
      if (!byUser[s.username]) byUser[s.username] = 0;
      byUser[s.username] += s.total;
    });
    return Object.entries(byUser)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [userStats]);

  // Process summary data for pie chart
  const typeData = React.useMemo(() => {
    return summary.map(s => ({
      name: s.stat_type,
      value: s.total
    }));
  }, [summary]);

  // Process timeline data for line chart
  const timelineData = React.useMemo(() => {
    const byDate = {};
    timelineStats.forEach(s => {
      if (!byDate[s.date]) byDate[s.date] = { date: s.date };
      byDate[s.date][s.stat_type] = s.total;
    });
    return Object.values(byDate).sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-14);
  }, [timelineStats]);

  const hasData = summary.length > 0;

  if (!hasData) {
    return <p className="empty-state">No stats yet. Add some stats to see analytics!</p>;
  }

  return (
    <div className="stats-tab">
      <div className="charts-grid">
        {/* Bar Chart - Top 10 users */}
        <div className="chart-card">
          <h3>By Person (Top 10)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={userData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={80} />
              <Tooltip />
              <Bar dataKey="total" fill="#667eea" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart - Stats by type */}
        <div className="chart-card">
          <h3>By Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={typeData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              >
                {typeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Line Chart - Timeline (only if enough data) */}
        {timelineData.length > 1 && (
          <div className="chart-card full-width">
            <h3>Timeline</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={timelineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                {summary.slice(0, 5).map((s, i) => (
                  <Line
                    key={s.stat_type_id}
                    type="monotone"
                    dataKey={s.stat_type}
                    stroke={COLORS[i]}
                    strokeWidth={2}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Table - Top pairs */}
        {pairStats.length > 0 && (
          <div className="chart-card full-width">
            <h3>Top Pairs (Who drinks together most)</h3>
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Person 1</th>
                  <th>Person 2</th>
                  <th>Total</th>
                  <th>Entries</th>
                </tr>
              </thead>
              <tbody>
                {pairStats.slice(0, 15).map((s, i) => (
                  <tr key={i}>
                    <td>{s.stat_type}</td>
                    <td>{s.user1}</td>
                    <td>{s.user2}</td>
                    <td>{s.total}</td>
                    <td>{s.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// RECENT STATS COMPONENT
// ============================================================================

/**
 * RecentStats Component
 * Shows recent stat entries with edit/delete functionality
 */
function RecentStats({ stats, users, statTypes, token, onRefresh }) {
  const [editingStat, setEditingStat] = React.useState(null);

  /**
   * Delete a stat entry
   */
  const handleDelete = async (id) => {
    if (!confirm('Delete this entry?')) return;
    
    const res = await fetch(`/api/stats/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (res.ok) {
      onRefresh();
    }
  };

  /**
   * Update a stat entry
   */
  const handleEdit = async (e) => {
    e.preventDefault();
    
    const res = await fetch(`/api/stats/${editingStat.id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        statTypeId: editingStat.stat_type_id,
        value: editingStat.value,
        participantIds: editingStat.participant_ids,
        date: editingStat.date ? new Date(editingStat.date).toISOString() : null
      })
    });
    
    if (res.ok) {
      setEditingStat(null);
      onRefresh();
    }
  };

  return (
    <div className="recent-stats">
      {/* Edit Modal */}
      {editingStat && (
        <div className="modal-overlay" onClick={() => setEditingStat(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Edit Stat</h2>
            <form onSubmit={handleEdit}>
              <div className="form-group">
                <label>Stat Type</label>
                <select 
                  value={editingStat.stat_type_id} 
                  onChange={e => setEditingStat({...editingStat, stat_type_id: parseInt(e.target.value)})}
                >
                  {statTypes.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Value</label>
                <input
                  type="number"
                  min="1"
                  value={editingStat.value}
                  onChange={e => setEditingStat({...editingStat, value: parseInt(e.target.value)})}
                />
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="datetime-local"
                  value={editingStat.date || ''}
                  onChange={e => setEditingStat({...editingStat, date: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Participants</label>
                <div className="participant-grid">
                  {users.map(u => (
                    <button
                      key={u.id}
                      type="button"
                      className={`participant-btn ${editingStat.participant_ids.includes(u.id) ? 'selected' : ''}`}
                      onClick={() => {
                        const ids = editingStat.participant_ids;
                        setEditingStat({
                          ...editingStat,
                          participant_ids: ids.includes(u.id) 
                            ? ids.filter(id => id !== u.id)
                            : [...ids, u.id]
                        });
                      }}
                    >
                      {u.username}
                    </button>
                  ))}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setEditingStat(null)}>Cancel</button>
                <button type="submit" className="submit-btn">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stats Table */}
      {stats.length === 0 ? (
        <p className="empty-state">No stats yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Value</th>
              <th>Who</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {stats.map((s, i) => (
              <tr key={i}>
                <td>{new Date(s.created_at).toLocaleDateString()}</td>
                <td>{s.stat_type}</td>
                <td>{s.value}</td>
                <td>{s.participants.join(', ')}</td>
                <td>
                  <button 
                    className="action-btn edit-btn" 
                    onClick={() => setEditingStat({...s, date: s.created_at ? s.created_at.slice(0, 16) : ''})}
                  >
                    Edit
                  </button>
                  <button className="action-btn delete-btn" onClick={() => handleDelete(s.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ============================================================================
// ADD STAT MODAL COMPONENT
// ============================================================================

/**
 * AddStatModal Component
 * Form modal for adding new stat entries
 */
function AddStatModal({ token, users, statTypes, onClose, onSuccess }) {
  // Form state
  const [selectedType, setSelectedType] = React.useState('');
  const [newType, setNewType] = React.useState('');
  const [value, setValue] = React.useState(1);
  const [date, setDate] = React.useState('');
  const [participants, setParticipants] = React.useState([]);
  const [error, setError] = React.useState('');

  /**
   * Handle form submission
   * Creates new stat type if needed, then creates stat entry
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const statTypeId = selectedType === '__new__' ? null : selectedType;
    
    // Validate stat type
    if (!statTypeId && !newType.trim()) {
      setError('Please select or create a stat type');
      return;
    }

    // Validate participants
    if (participants.length === 0) {
      setError('Please select at least one participant');
      return;
    }

    let finalTypeId = statTypeId;

    // Create new stat type if selected
    if (newType.trim()) {
      const res = await fetch('/api/stat-types', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: newType.trim() })
      });
      
      if (!res.ok) {
        setError('Failed to create stat type');
        return;
      }
      
      const data = await res.json();
      finalTypeId = data.id;
    }

    // Create stat entry
    const res = await fetch('/api/stats', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        statTypeId: finalTypeId,
        value: parseInt(value),
        participantIds: participants,
        date: date ? new Date(date).toISOString() : null
      })
    });

    if (!res.ok) {
      setError('Failed to add stat');
      return;
    }

    onSuccess();
  };

  /**
   * Toggle participant selection
   */
  const toggleParticipant = (userId) => {
    setParticipants(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2>Add Stat</h2>
        <form onSubmit={handleSubmit}>
          {/* Stat Type Selection */}
          <div className="form-group">
            <label>Stat Type</label>
            <select value={selectedType} onChange={e => setSelectedType(e.target.value)}>
              <option value="">Select type...</option>
              {statTypes.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
              <option value="__new__">+ Create new type</option>
            </select>
            {/* Show input for new type when selected */}
            {selectedType === '__new__' && (
              <input
                type="text"
                placeholder="New stat type (e.g., Beer, Shot)"
                value={newType}
                onChange={e => setNewType(e.target.value)}
              />
            )}
          </div>

          {/* Value Input */}
          <div className="form-group">
            <label>Value</label>
            <input
              type="number"
              min="1"
              value={value}
              onChange={e => setValue(e.target.value)}
            />
          </div>

          {/* Date Picker (Optional) */}
          <div className="form-group">
            <label>Date (optional - leave empty for current time)</label>
            <input
              type="datetime-local"
              value={date}
              onChange={e => setDate(e.target.value)}
            />
          </div>

          {/* Participant Selection */}
          <div className="form-group">
            <label>Participants ({participants.length} selected)</label>
            <div className="participant-grid">
              {users.map(u => (
                <button
                  key={u.id}
                  type="button"
                  className={`participant-btn ${participants.includes(u.id) ? 'selected' : ''}`}
                  onClick={() => toggleParticipant(u.id)}
                >
                  {u.username}
                </button>
              ))}
            </div>
          </div>

          {/* Error Message */}
          {error && <p className="error">{error}</p>}

          {/* Action Buttons */}
          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="submit-btn">Add Stat</button>
          </div>
        </form>
      </div>
    </div>
  );
}
